import PropTypes from 'prop-types'
import React, { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import axios from 'axios'
import URI from 'urijs'
import fileDownload from 'js-file-download'
import { useParams, Link } from 'react-router-dom'
import moment from 'moment'
import pluralize from 'pluralize'
import { capitalize } from 'lodash'

import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { Checkbox } from '../../ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../ui/tabs'
import CreditCardManager from '../CreditCardManager/CreditCardManager'
import AddressInput from '../AddressInput/AddressInput'
import Notification from '../Notification/Notification'
import { priceInDollars } from '../../../utilities/helpers'
import toast from '../../../utilities/toast'

function FacilityEditForm({
  onSubmit,
  id,
  name,
  address = {},
  curriculums: _curriculums = [],
  stripeCreditCardBrand,
  stripeCreditCardLast4,
  onCardChange,
  isStripeConnected,
  upcomingInvoiceStart,
  upcomingInvoiceEnd,
  upcomingInvoiceTotal,
  upcomingInvoiceStudentsCount,
  isSubscriptionCancelledAtPeriodEnd,
  subscriptionStatus,
  demo = false,
  trialEnd,
  newRecord: _newRecord = false,
  providesFreeRentalSkates,
  supportsSkating
}) {
  const { facility_id: facilityId } = useParams()
  const [showStripeConnected, setShowStripeConnected] = useState(isStripeConnected)
  const [activeTab, setActiveTab] = useState('general')
  const activeOrTrialing = ['active', 'trialing'].includes(subscriptionStatus)
  const [isGuardianExportDownloading, setIsGuardianExportDownloading] = useState(false)
  const [isNameBadgeExportDownloading, setIsNameBadgeExportDownloading] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      name: name || '',
      address: address || {},
      providesFreeRentalSkates: providesFreeRentalSkates || false
    }
  })

  useEffect(() => {
    setShowStripeConnected(isStripeConnected)
  }, [isStripeConnected])

  const stripeButtonURI = URI({
    protocol: 'https',
    hostname: 'dashboard.stripe.com',
    path: '/oauth/authorize',
    query: {
      response_type: 'code',
      client_id: process.env.STRIPE_CONNECT_CLIENT_ID,
      scope: 'read_write',
      state: id,
      redirect_uri: `${window.location.origin}/api/stripe/new`
    }
  })

  async function handleFormSubmit(data) {
    await onSubmit(data)
  }

  function handleGuardianExportDownload() {
    setIsGuardianExportDownloading(true)
    axios
      .get(`/api/facilities/${facilityId}/guardian_exports`, { responseType: 'blob' })
      .then((response) => {
        const header = response.headers['content-disposition']
        const [_, filename] = header.match(/filename="(.+)";/)
        fileDownload(response.data, filename)
        toast.success('Guardian contact list downloaded successfully!')
      })
      .catch((_error) => {
        toast.error('Failed to download guardian contact list. Please try again.')
      })
      .finally(() => setIsGuardianExportDownloading(false))
  }

  function handleNameBadgeDownload() {
    setIsNameBadgeExportDownloading(true)
    axios
      .post(`/api/facilities/${facilityId}/name_badge_exports`, { responseType: 'blob' })
      .then((response) => {
        const header = response.headers['content-disposition']
        const [_, filename] = header.match(/filename="(.+)";/)
        fileDownload(response.data, filename)
        toast.success('Name badge data downloaded successfully!')
      })
      .catch((_error) => {
        toast.error('Failed to download name badge data. Please try again.')
      })
      .finally(() => setIsNameBadgeExportDownloading(false))
  }

  return (
    <div className="flex flex-col flex-1 bg-gray-100">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex p-1 mb-6 space-x-1 shadow-inner bg-gray-200/50 rounded-xl w-full sm:w-96">
            <TabsTrigger value="general" className="flex-1">
              {capitalize('general')}
            </TabsTrigger>
            <TabsTrigger value="billing" className="flex-1">
              {capitalize('billing')}
            </TabsTrigger>
            <TabsTrigger value="export" className="flex-1 text-xs sm:text-sm">
              {capitalize('data export')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general">
            <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Facility Information</CardTitle>
                  <CardDescription>
                    General facility information that is displayed to all parents, directors and coaches.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">
                      Facility Name <span className="text-red-500">*</span>
                    </Label>
                    <Controller
                      name="name"
                      control={control}
                      rules={{ required: 'Facility name is required' }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          id="name"
                          type="text"
                          placeholder="Your facility's name"
                          className={errors.name ? 'border-red-500 focus-visible:ring-red-500' : ''}
                          autoFocus
                        />
                      )}
                    />
                    {errors.name && <p className="text-xs text-red-500">{errors.name.message}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address">Location</Label>
                    <Controller
                      name="address"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (!value || !value.line1 || !value.city || !value.state || !value.postCode) {
                            return 'Complete address is required'
                          }
                          return true
                        }
                      }}
                      render={({ field }) => (
                        <AddressInput {...field} label="" errorMessage={errors.address ? errors.address.message : ''} />
                      )}
                    />
                  </div>

                  {supportsSkating && (
                    <div className="flex items-center space-x-2">
                      <Controller
                        name="providesFreeRentalSkates"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <Checkbox
                            id="providesFreeRentalSkates"
                            checked={value}
                            onCheckedChange={onChange}
                            data-cy="providesFreeRentalSkates-checkbox-input"
                          />
                        )}
                      />
                      <div className="space-y-1">
                        <Label htmlFor="providesFreeRentalSkates" className="cursor-pointer">
                          Provide rental skates?
                        </Label>
                        <Controller
                          name="providesFreeRentalSkates"
                          control={control}
                          render={({ field: { value } }) => (
                            <p className="text-xs text-gray-500">
                              {value ? 'We will' : "We won't"} ask students for skate type and size when registering.
                            </p>
                          )}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto px-8">
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="billing">
            {demo ? (
              <Card>
                <CardHeader>
                  <CardTitle>Demo</CardTitle>
                  <CardDescription>Billing and Stripe connections are disabled in the shared demo.</CardDescription>
                </CardHeader>
              </Card>
            ) : (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Subscription Status</CardTitle>
                    <CardDescription>View your current subscription status and billing information.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {!activeOrTrialing && (
                      <div className="text-sm space-y-2" data-cy="no-subscription-notice">
                        <p>You don&apos;t have an active subscription.</p>
                        <p>To start using Leevo, enter a new credit card below.</p>
                      </div>
                    )}

                    {subscriptionStatus === 'trialing' && (
                      <div data-cy="trialing-notice" className="text-sm space-y-3">
                        <p>
                          Your free trial will end on{' '}
                          <span className="font-semibold">{moment.unix(trialEnd).format('MMMM Do, YYYY')}</span>.
                        </p>
                        <p>
                          You won&apos;t be charged until your trial ends.{' '}
                          {stripeCreditCardBrand && 'If you no longer want to use Leevo, remove your card.'}
                          {!stripeCreditCardBrand && 'To access Leevo after your trial ends, add a credit card.'}
                        </p>
                      </div>
                    )}

                    {subscriptionStatus === 'active' && (
                      <div data-cy="active-subscription-info" className="text-sm space-y-3">
                        <p>
                          Upcoming bill: <span className="font-semibold">${priceInDollars(upcomingInvoiceTotal)}</span>
                        </p>
                        <p>
                          Number of students:{' '}
                          <span className="font-semibold">
                            {pluralize('student', upcomingInvoiceStudentsCount, true)}
                          </span>
                        </p>
                        <p>
                          Billing period start:{' '}
                          <span className="font-semibold">
                            {moment.unix(upcomingInvoiceStart).format('MMMM Do, YYYY')}
                          </span>
                        </p>
                        <p>
                          Billing period end:{' '}
                          <span className="font-semibold">
                            {moment.unix(upcomingInvoiceEnd).format('MMMM Do, YYYY')}
                          </span>
                        </p>
                        <p className="text-xs text-gray-600">
                          If you enter the next{' '}
                          <Link className="font-semibold text-purple-600 hover:text-purple-400" to="/pricing">
                            pricing tier
                          </Link>{' '}
                          before the end of your billing period, then your price may increase.
                        </p>
                      </div>
                    )}

                    {isSubscriptionCancelledAtPeriodEnd && (
                      <Notification show color="red" className="text-sm" data-cy="cancellation-notification">
                        <p className="font-medium">
                          {subscriptionStatus === 'active' && 'Your subscription is not active.'}
                          {subscriptionStatus === 'trialing' && 'Your subscription will cancel after your trial.'}
                        </p>
                        <p className="mt-2 font-medium">
                          You will lose access to Leevo on{' '}
                          <span className="font-semibold">
                            {moment.unix(upcomingInvoiceEnd || trialEnd).format('MMMM Do, YYYY')}
                          </span>
                          .
                        </p>
                        <p className="mt-2 font-medium">
                          To ensure you have access to Leevo, update your credit card info.
                        </p>
                      </Notification>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Payment Method</CardTitle>
                    <CardDescription>
                      Manage the payment method on file that will cover your monthly fee, based on the number of
                      students who have taken a class in the past month.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {onCardChange && (
                      <CreditCardManager
                        brand={activeOrTrialing ? stripeCreditCardBrand : null}
                        lastFour={activeOrTrialing ? stripeCreditCardLast4 : null}
                        onChange={onCardChange}
                      />
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Payment Processing</CardTitle>
                    <CardDescription>
                      Connect your Stripe account to Leevo to enable registration, which automatically places students
                      into classes.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {showStripeConnected && (
                      <div className="space-y-3">
                        <p className="text-sm text-red-700">
                          Clicking this button will disconnect Stripe from your account.
                          <br />
                          That means you will no longer be able to register new students using Leevo.
                        </p>
                        <Button
                          data-cy="disconnect-stripe-button"
                          variant="outline"
                          className="w-full sm:w-auto border-red-500 text-red-700 hover:bg-red-50"
                          onClick={() => axios.delete(`/api/stripe/${id}`).then(() => setShowStripeConnected(false))}
                        >
                          Disconnect Stripe Account
                        </Button>
                      </div>
                    )}
                    {!showStripeConnected && (
                      <a href={stripeButtonURI} className="stripe-connect light-blue" data-cy="connect-stripe-button">
                        <span>Connect with Stripe</span>
                      </a>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="export">
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Guardian Contact List</CardTitle>
                  <CardDescription>
                    Download a list of the guardians of all currently enrolled students.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    variant="outline"
                    type="button"
                    disabled={isGuardianExportDownloading}
                    className="w-full sm:w-auto"
                    onClick={handleGuardianExportDownload}
                  >
                    {isGuardianExportDownloading ? 'Preparing...' : 'Download'}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Name Badge Data</CardTitle>
                  <CardDescription>
                    Download a list of all students in upcoming classes, with their skill level.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    variant="outline"
                    type="button"
                    disabled={isNameBadgeExportDownloading}
                    className="w-full sm:w-auto"
                    onClick={handleNameBadgeDownload}
                  >
                    {isNameBadgeExportDownloading ? 'Preparing...' : 'Download'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}

FacilityEditForm.propTypes = {
  demo: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  name: PropTypes.string,
  address: PropTypes.shape({
    line1: PropTypes.string,
    line2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postCode: PropTypes.string
  }),
  curriculums: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string
    })
  ),
  stripeCreditCardBrand: PropTypes.string,
  stripeCreditCardLast4: PropTypes.string,
  trialEnd: PropTypes.number,
  isStripeConnected: PropTypes.bool,
  isSubscriptionCancelledAtPeriodEnd: PropTypes.bool,
  subscriptionStatus: PropTypes.string,
  upcomingInvoiceStart: PropTypes.number,
  upcomingInvoiceEnd: PropTypes.number,
  upcomingInvoiceTotal: PropTypes.number,
  upcomingInvoiceStudentsCount: PropTypes.number,
  onSubmit: PropTypes.func.isRequired,
  onCardChange: PropTypes.func,
  newRecord: PropTypes.bool,
  providesFreeRentalSkates: PropTypes.bool,
  supportsSkating: PropTypes.bool
}

export default FacilityEditForm
